import type { ConversationCapabilities } from './permissions';
import type { Role } from './enums';
import { CLOSED_TASK_STATUSES, type TaskKind, type TaskStatus, type TaskVisibility } from './tasks';

/**
 * Task read visibility and field-edit rules (PLAN §3.2 "Visibility rules for tasks" and "Field
 * edit permissions", D-037, D-054). Pure: the API loads the facts (`TaskViewer` from the request
 * context, the org closure, department and project memberships; `TaskVisibilityFacts` from the
 * task row) and calls these; its SQL list filter (`visibleTaskFilter`) implements the same rules,
 * and a table-driven test keeps the two in agreement. The web app may call them to hide controls,
 * but the server decides.
 */

/** Everything about the viewer that the rules read. */
export interface TaskViewer {
  userId: string;
  role: Role;
  /**
   * Transitive reports (`orgService.getReportsTree(viewer)`, flattened): the viewer sees every
   * non-draft task owned or delegated by one of them (rule 4).
   */
  subordinateIds: ReadonlySet<string>;
  /** The viewer's department(s) (`orgService.getDepartment`, D-007): rule 5. */
  departmentIds: ReadonlySet<string>;
  /** Projects the viewer can see (memberships + every `workspace` project unless a guest): rule 6. */
  projectIds: ReadonlySet<string>;
  /** `member_grants.grant = 'cockpit'` (D-036): rule 8, read-only. */
  hasCockpitGrant: boolean;
  /** Phase 7: meeting occurrences the viewer attended (rule 7). Empty until meetings exist. */
  meetingOccurrenceIds?: ReadonlySet<string>;
}

/** The task columns and relations the rules read. */
export interface TaskVisibilityFacts {
  status: TaskStatus;
  ownerUserId: string;
  delegatedByUserId: string | null;
  createdByUserId: string;
  visibility: TaskVisibility;
  departmentId: string | null;
  projectId: string | null;
  sourceMeetingOccurrenceId?: string | null;
  watcherIds: readonly string[];
  /** Owners of the task's checklist items. */
  checklistOwnerIds: readonly string[];
}

export function isAdminRole(role: Role): boolean {
  return role === 'owner' || role === 'admin';
}

/** Owner, delegator, creator, watchers and checklist-item owners (rule 3). */
export function isTaskParticipant(userId: string, task: TaskVisibilityFacts): boolean {
  return (
    task.ownerUserId === userId ||
    task.delegatedByUserId === userId ||
    task.createdByUserId === userId ||
    task.watcherIds.includes(userId) ||
    task.checklistOwnerIds.includes(userId)
  );
}

/** The participants to notify and to keep as `conversation_members` of the task thread. */
export function taskParticipantIds(task: TaskVisibilityFacts): string[] {
  const ids = [
    task.ownerUserId,
    task.delegatedByUserId,
    task.createdByUserId,
    ...task.watcherIds,
    ...task.checklistOwnerIds,
  ].filter((id): id is string => id !== null);
  return [...new Set(ids)];
}

/**
 * PLAN §3.2 rules 1–8 (D-037). Deactivated members never get here (authentication refuses them).
 *
 * 1. Drafts: only the creator. 2. Admin / Owner: every non-draft task. 3. Participants (guests
 * included). 4. The manager chain of the owner or the delegator (non-guests). 5–8 only for
 * `visibility = 'department'` and non-guests: the task's department, the project audience, the
 * source meeting's attendees, cockpit grantees.
 */
export function canViewTask(viewer: TaskViewer, task: TaskVisibilityFacts): boolean {
  if (task.status === 'draft') return task.createdByUserId === viewer.userId;
  if (isAdminRole(viewer.role)) return true;
  if (isTaskParticipant(viewer.userId, task)) return true;
  if (viewer.role === 'guest') return false;
  if (viewer.subordinateIds.has(task.ownerUserId)) return true;
  if (task.delegatedByUserId !== null && viewer.subordinateIds.has(task.delegatedByUserId)) {
    return true;
  }
  if (task.visibility !== 'department') return false;
  if (task.departmentId !== null && viewer.departmentIds.has(task.departmentId)) return true;
  if (task.projectId !== null && viewer.projectIds.has(task.projectId)) return true;
  if (viewer.hasCockpitGrant) return true;
  const meeting = task.sourceMeetingOccurrenceId ?? null;
  return meeting !== null && (viewer.meetingOccurrenceIds?.has(meeting) ?? false);
}

/**
 * The task comment conversation (`conversations.kind = 'task'`, D-054): access follows task
 * visibility, not membership. Any viewer may read, comment and react (guests only see tasks they
 * take part in, so for them this is the participant rule); membership rows only carry unread
 * state and notification preferences.
 */
export function taskConversationCapabilities(input: {
  canView: boolean;
  isMember: boolean;
}): ConversationCapabilities {
  const { canView, isMember } = input;
  return {
    isMember,
    isChannelManager: false,
    canRead: canView,
    canPost: canView,
    // A task comment stream is all thread, and commenting IS the point (D-1214).
    canReplyInThread: canView,
    canReact: canView,
    /*
     * D-1212: never, whatever `canView` says. A task's comment stream is not a room you can ring
     * — `conversationCallPolicy('task')` already answers `allowed: false` — and elsewhere this
     * capability is `canPost`, which here would claim the opposite. The two must not disagree.
     */
    canStartCall: false,
    canJoin: false,
    canLeave: false,
    canRename: false,
    canEditTopic: false,
    canArchive: false,
    canUnarchive: false,
    canAddMembers: false,
    canAddGuests: false,
    canRemoveMembers: false,
    canChangeMemberRoles: false,
    canConvertToPublic: false,
    canConvertToPrivate: false,
    canDeleteOthersMessages: false,
    canUpdateMemberState: canView && isMember,
  };
}

/**
 * Whether a source-thread post or a chip may show more than `{ number, status }` (PLAN §3.2
 * "Source-thread posting"): never for decision requests or participants-only tasks.
 */
export function isTaskSourceRestricted(task: {
  kind: TaskKind;
  visibility: TaskVisibility;
}): boolean {
  return task.kind === 'decision_request' || task.visibility === 'participants';
}

// ---------------------------------------------------------------- field edit permissions

/** How the actor relates to the task for field edits (the state machine's actor roles, plus the chain). */
export type TaskFieldActor =
  'creator' | 'delegator' | 'owner' | 'self_owner' | 'admin' | 'manager_chain';

/** The `PATCH /tasks/:taskId` fields and the related sub-resources (PLAN §3.2 table). */
export const TASK_EDITABLE_FIELDS = [
  'title',
  'definitionOfDone',
  'priority',
  'description',
  'checklist',
  'attachments',
  'projectId',
  'milestoneId',
  'departmentId',
  'kraId',
  'visibility',
  'postUpdatesToSource',
  'decision',
] as const;
export type TaskEditableField = (typeof TASK_EDITABLE_FIELDS)[number];

type Phase = 'draft' | 'proposed' | 'active' | 'closed';

function phaseOf(status: TaskStatus): Phase {
  if (status === 'draft') return 'draft';
  if (status === 'proposed') return 'proposed';
  return (CLOSED_TASK_STATUSES as readonly string[]).includes(status) ? 'closed' : 'active';
}

const D_OR_SELF = ['delegator', 'self_owner'] as const;

/**
 * PLAN §3.2 "Field edit permissions": who may change a field in each phase. For self tasks the
 * owner has every delegator right (`self_owner`). Watchers are separate (any viewer may add or
 * remove themselves; owner, delegator and admins may change anyone). Due dates, owner and status
 * have their own actions. Returns false for guests except checklist items they own (checked by
 * the checklist service).
 */
export function canEditTaskField(
  field: TaskEditableField,
  task: { status: TaskStatus; kind: TaskKind; awaitingParty?: 'owner' | 'delegator' | null },
  actors: ReadonlySet<TaskFieldActor>,
): boolean {
  const phase = phaseOf(task.status);
  const has = (...roles: readonly TaskFieldActor[]) => roles.some((r) => actors.has(r));
  switch (field) {
    case 'title':
    case 'definitionOfDone':
    case 'priority':
      if (phase === 'draft') return has('creator');
      if (phase === 'closed') return false;
      return has(...D_OR_SELF);
    case 'description':
    case 'checklist':
    case 'attachments':
      if (phase === 'draft') return has('creator');
      if (phase === 'closed') return false;
      return has(...D_OR_SELF, 'owner');
    case 'projectId':
    case 'milestoneId':
    case 'departmentId':
      if (phase === 'draft') return has('creator');
      if (phase === 'closed') return has(...D_OR_SELF);
      return has(...D_OR_SELF, 'owner');
    case 'kraId':
      if (phase === 'draft') return false;
      return has('owner', 'manager_chain');
    case 'visibility':
    case 'postUpdatesToSource':
      if (phase === 'draft') return has('creator');
      return has(...D_OR_SELF, 'admin');
    case 'decision':
      // Options are editable only while Draft or awaiting the requester (PLAN §3.2).
      if (task.kind !== 'decision_request') return false;
      if (phase === 'draft') return has('creator');
      return task.status === 'proposed' && task.awaitingParty === 'delegator' && has('delegator');
  }
}
